import {createContext, ReactNode, useContext, useState, useEffect} from "react";
import {NoteType, NoteTypeSection, NoteTypeScope, Optional} from "../utils/types.ts";
import {getNoteTypes, setDefaultNoteType as apiSetDefaultNoteType, setDefaultFollowupNoteType as apiSetDefaultFollowupNoteType, getDefaultFollowupNoteType as apiGetDefaultFollowupNoteType, createNoteType as apiCreateNoteType, updateNoteType as apiUpdateNoteType, archiveNoteType as apiArchiveNoteType} from "../api/notes.ts";
import {AlertsContext} from "./AlertsContext.tsx";
import {getEventBus, EventTypes, NoteTypePayload, NoteTypeArchivedPayload, SyncPayload} from "../services/EventBus.ts";

export type WebsocketNoteTypePayload = NoteType;

type NoteTypesContextType = {
  noteTypes: Optional<NoteType[]>;
  setNoteTypes: (noteTypes: Optional<NoteType[]>) => void;
  getNoteType: (noteTypeId: number) => Promise<Optional<NoteType>>;
  fetchNoteTypes: () => Promise<NoteType[]>;
  setDefaultNoteType: (noteTypeId: number) => void;
  getDefaultNoteType: () => Promise<Optional<NoteType>>;
  defaultNoteType: Optional<NoteType>;
  updateDefaultNoteType: (newData: WebsocketNoteTypePayload) => void;
  setDefaultFollowupNoteType: (noteTypeId: number | null) => void;
  getDefaultFollowupNoteType: () => Promise<Optional<NoteType>>;
  defaultFollowupNoteType: Optional<NoteType>;
  updateDefaultFollowupNoteType: (newData: WebsocketNoteTypePayload | null) => void;
  createNoteType: (name: string, scope?: NoteTypeScope, sections?: NoteTypeSection[]) => Promise<NoteType>;
  updateNoteType: (noteTypeId: number, name?: string, scope?: NoteTypeScope, sections?: NoteTypeSection[]) => Promise<NoteType>;
  archiveNoteType: (noteTypeId: number) => Promise<void>;
  customNoteTypes: NoteType[];
  predefinedNoteTypes: NoteType[];
}

const contextDefaultValues: NoteTypesContextType = {
  noteTypes: null,
  setNoteTypes: () => {},
  getNoteType: async () => null,
  fetchNoteTypes: async () => [],
  setDefaultNoteType: () => {},
  getDefaultNoteType: async () => null,
  defaultNoteType: null,
  updateDefaultNoteType: () => {},
  setDefaultFollowupNoteType: () => {},
  getDefaultFollowupNoteType: async () => null,
  defaultFollowupNoteType: null,
  updateDefaultFollowupNoteType: () => {},
  createNoteType: async () => { throw new Error('Not implemented'); },
  updateNoteType: async () => { throw new Error('Not implemented'); },
  archiveNoteType: async () => {},
  customNoteTypes: [],
  predefinedNoteTypes: []
}

const NoteTypesContext = createContext<NoteTypesContextType>(contextDefaultValues);

function NoteTypesProvider({ children }: { children: ReactNode }) {
  const [noteTypes, setNoteTypes] = useState<Optional<NoteType[]>>(null);
  const [defaultNoteType, setDefaultNoteType] = useState<Optional<NoteType>>(null);
  const [defaultFollowupNoteType, setDefaultFollowupNoteType] = useState<Optional<NoteType>>(null);
  const { showAlert } = useContext(AlertsContext);

  // Computed properties for filtering note types
  const customNoteTypes = noteTypes?.filter(nt => !nt.is_public && !nt.is_archived) || [];
  const predefinedNoteTypes = noteTypes?.filter(nt => nt.is_public && !nt.is_archived) || [];

  // Subscribe to WebSocket events for note type changes
  useEffect(() => {
    const eventBus = getEventBus();

    // Load note types from initial sync
    const syncSub = eventBus.subscribe(EventTypes.SYNC, (payload: SyncPayload) => {
      const syncedNoteTypes = payload.note_types.map(payloadToNoteType);
      setNoteTypes(syncedNoteTypes);

      // Set defaults from synced data
      const defaultNt = syncedNoteTypes.find(nt => nt.is_default);
      if (defaultNt) setDefaultNoteType(defaultNt);
    });

    const createdSub = eventBus.subscribe(EventTypes.NOTE_TYPE_CREATED, (payload: NoteTypePayload) => {
      handleNoteTypeCreated(payload);
    });

    const updatedSub = eventBus.subscribe(EventTypes.NOTE_TYPE_UPDATED, (payload: NoteTypePayload) => {
      handleNoteTypeUpdated(payload);
    });

    const archivedSub = eventBus.subscribe(EventTypes.NOTE_TYPE_ARCHIVED, (payload: NoteTypeArchivedPayload) => {
      handleNoteTypeArchived(payload);
    });

    const followupChangedSub = eventBus.subscribe(EventTypes.DEFAULT_FOLLOWUP_NOTE_TYPE_CHANGED, (payload: NoteTypePayload | null) => {
      handleDefaultFollowupNoteTypeChanged(payload);
    });

    return () => {
      syncSub.unsubscribe();
      createdSub.unsubscribe();
      updatedSub.unsubscribe();
      archivedSub.unsubscribe();
      followupChangedSub.unsubscribe();
    };
  }, []);

  function handleNoteTypeCreated(payload: NoteTypePayload) {
    const newNoteType = payloadToNoteType(payload);
    setNoteTypes(prev => prev ? [...prev, newNoteType] : [newNoteType]);
  }

  function handleNoteTypeUpdated(payload: NoteTypePayload) {
    const updatedNoteType = payloadToNoteType(payload);
    setNoteTypes(prev => {
      if (!prev) return prev;
      const index = prev.findIndex(nt => nt.id === updatedNoteType.id);
      if (index === -1) return prev;
      const copy = [...prev];
      copy[index] = { ...copy[index], ...updatedNoteType };
      return copy;
    });
  }

  function handleNoteTypeArchived(payload: NoteTypeArchivedPayload) {
    setNoteTypes(prev => {
      if (!prev) return prev;
      return prev.filter(nt => nt.id !== payload.id);
    });
  }

  function handleDefaultFollowupNoteTypeChanged(payload: NoteTypePayload | null) {
    if (payload === null) {
      setDefaultFollowupNoteType(null);
      setNoteTypes(prev => {
        if (!prev) return prev;
        return prev.map(nt => ({ ...nt, is_default_followup: false }));
      });
    } else {
      const noteType = payloadToNoteType(payload);
      setDefaultFollowupNoteType(noteType);
      setNoteTypes(prev => {
        if (!prev) return prev;
        return prev.map(nt => ({
          ...nt,
          is_default_followup: nt.id === noteType.id
        }));
      });
    }
  }

  function payloadToNoteType(payload: NoteTypePayload): NoteType {
    return {
      id: payload.id,
      name: payload.name,
      description: payload.description || '',
      template: payload.template as NoteType['template'],
      version: payload.version,
      is_public: payload.is_public,
      user_id: payload.user_id,
      organization_id: payload.organization_id,
      created_at: '',
      updated_at: '',
      is_default: false,
      is_editable: payload.is_editable,
      is_archived: payload.is_archived,
      is_organization_default: false,
      is_organization_default_followup: false,
      sections: payload.sections || []
    };
  }

  async function getNoteType(noteTypeId: number): Promise<Optional<NoteType>> {
    if(!noteTypes) {
      const fetched = await fetchNoteTypes();
      return fetched.find(nt => nt.id === noteTypeId) || null;
    }
    return noteTypes.find(nt => nt.id === noteTypeId) || null;
  }

  async function fetchNoteTypes(): Promise<NoteType[]> {
    const fetchedNoteTypes: NoteType[] = await getNoteTypes();
    setNoteTypes(fetchedNoteTypes);
    return fetchedNoteTypes;
  }

  function setDefault(noteTypeId: number) {
    if(!noteTypeId || !noteTypes) return;

    const copy = [...noteTypes];
    const previousDefault = copy?.find(noteType => noteType.is_default);
    const newDefault = copy?.find(noteType => noteType.id === noteTypeId);

    if(previousDefault) {
      if (previousDefault.id === noteTypeId) return;
      previousDefault.is_default = false;
    }

    if (newDefault) {
      newDefault.is_default = true;
      setDefaultNoteType(newDefault);
    }
    apiSetDefaultNoteType(noteTypeId)
      .then(() => { showAlert({ message: 'Default note type set successfully!', type: 'success' }) });

    document.startViewTransition ?
      document.startViewTransition(() => setNoteTypes(copy)) :
      setNoteTypes(copy);
  }

  async function getDefaultNoteType(): Promise<Optional<NoteType>> {
    if(!noteTypes) {
      const fetched = await fetchNoteTypes();
      const fetchedDefault = fetched.find(nt => nt.is_default) || null;
      setDefaultNoteType(fetchedDefault);
      return fetchedDefault;
    }
    const def =  noteTypes.find(nt => nt.is_default) || null;
    setDefaultNoteType(def);
    return def;
  }

  function updateDefaultNoteType(newData: WebsocketNoteTypePayload) {
    setDefaultNoteType(newData);
    setNoteTypes(prev => {
      if(!prev) return prev;
      const copy = [...prev];
      const prevDefaultIndex = copy.findIndex(noteType => noteType.is_default);
      const newDefaultIndex = copy.findIndex(noteType => noteType.id === newData.id);
      if (prevDefaultIndex >= 0) copy[prevDefaultIndex].is_default = false;
      if (newDefaultIndex >= 0) copy[newDefaultIndex].is_default = true;
      return copy;
    });
  }

  function setDefaultFollowup(noteTypeId: number | null) {
    if(!noteTypes) return;

    const copy = [...noteTypes];
    // Clear previous default followup
    copy.forEach(nt => {
      if ((nt as any).is_default_followup) {
        (nt as any).is_default_followup = false;
      }
    });

    if (noteTypeId !== null) {
      const newDefault = copy.find(noteType => noteType.id === noteTypeId);
      if (newDefault) {
        (newDefault as any).is_default_followup = true;
        setDefaultFollowupNoteType(newDefault);
      }
    } else {
      setDefaultFollowupNoteType(null);
    }

    apiSetDefaultFollowupNoteType(noteTypeId)
      .then(() => { showAlert({ message: noteTypeId ? 'Default follow-up note type set successfully!' : 'Default follow-up note type cleared.', type: 'success' }) });

    document.startViewTransition ?
      document.startViewTransition(() => setNoteTypes(copy)) :
      setNoteTypes(copy);
  }

  async function getDefaultFollowupNoteType(): Promise<Optional<NoteType>> {
    const fetched = await apiGetDefaultFollowupNoteType();
    setDefaultFollowupNoteType(fetched);
    return fetched;
  }

  function updateDefaultFollowupNoteType(newData: WebsocketNoteTypePayload | null) {
    if (newData === null) {
      setDefaultFollowupNoteType(null);
    } else {
      setDefaultFollowupNoteType(newData);
    }
    setNoteTypes(prev => {
      if(!prev) return prev;
      return prev.map(nt => ({
        ...nt,
        is_default_followup: newData ? nt.id === newData.id : false
      }));
    });
  }

  async function createNoteType(name: string, scope: NoteTypeScope = 'organization', sections: NoteTypeSection[] = []): Promise<NoteType> {
    const created = await apiCreateNoteType({ name, scope, sections });
    // Note: The WebSocket event will update the state
    showAlert({ message: 'Note type created successfully!', type: 'success' });
    return created;
  }

  async function updateNoteType(noteTypeId: number, name?: string, scope?: NoteTypeScope, sections?: NoteTypeSection[]): Promise<NoteType> {
    const updated = await apiUpdateNoteType(noteTypeId, { name, scope, sections });
    // Note: The WebSocket event will update the state
    showAlert({ message: 'Note type updated successfully!', type: 'success' });
    return updated;
  }

  async function archiveNoteType(noteTypeId: number): Promise<void> {
    await apiArchiveNoteType(noteTypeId);
    // Note: The WebSocket event will update the state
    showAlert({ message: 'Note type deleted successfully!', type: 'success' });
  }

  return (
    <NoteTypesContext.Provider value={{
      noteTypes,
      setNoteTypes,
      getNoteType,
      fetchNoteTypes,
      setDefaultNoteType: setDefault,
      getDefaultNoteType,
      defaultNoteType,
      updateDefaultNoteType,
      setDefaultFollowupNoteType: setDefaultFollowup,
      getDefaultFollowupNoteType,
      defaultFollowupNoteType,
      updateDefaultFollowupNoteType,
      createNoteType,
      updateNoteType,
      archiveNoteType,
      customNoteTypes,
      predefinedNoteTypes
    }}>
      {children}
    </NoteTypesContext.Provider>
  );
}

export { NoteTypesProvider, NoteTypesContext };